import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  Edit3,
  UserCheck,
  ArrowLeft,
  Crown,
  Shield,
  Users,
  Trophy,
  Coins,
  ExternalLink,
  MessageCircle,
  Globe,
  Settings,
  UserPlus,
  UserMinus,
  ArrowRightLeft,
  Save,
  X,
  Search,
  History,
  LogOut,
} from "lucide-react";
import {
  fetchTeamHub,
  updateTeamHubSettings,
  uploadTeamBranding,
  deleteTeamBranding,
  searchTeamPlayers,
  inviteTeamPlayer,
  kickTeamPlayer,
  leaveTeam,
  transferTeamOwnership,
} from "@/lib/api";

export default function TeamDetails() {
  const { serverId, teamId } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageError, setMessageError] = useState("");

  const [nameDraft, setNameDraft] = useState("");
  const [descriptionDraft, setDescriptionDraft] = useState("");
  const [tagDraft, setTagDraft] = useState("");

  const [brandingLoading, setBrandingLoading] = useState({
    logo: false,
    banner: false,
  });

  const [brandingMessage, setBrandingMessage] = useState("");
  const [brandingError, setBrandingError] = useState("");

  const [inviteQuery, setInviteQuery] = useState("");
  const [invitePlayers, setInvitePlayers] = useState([]);
  const [inviteLoading, setInviteLoading] = useState(false);

  useEffect(() => {
    if (!serverId || !teamId) return;

    setLoading(true);
    setError(false);

    fetchTeamHub(serverId, teamId)
      .then((result) => {
        setData(result);
        setNameDraft(
          result?.team?.display_name ||
          result?.team?.name ||
          ""
        );

        setDescriptionDraft(
          result?.team?.custom_description ||
          result?.team?.description ||
          ""
        );

        setTagDraft(result?.team?.tag || "");
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [serverId, teamId]);

  if (loading) {
    return (
      <div className="pt-40 pb-24 text-center text-white/50">
        Loading team…
      </div>
    );
  }

  if (error || !data?.team) {
    return (
      <div className="pt-40 pb-24 text-center">
        <div className="text-white/50 mb-5">
          Team not found or is not publicly available.
        </div>

        <button
          type="button"
          onClick={() => {
  sessionStorage.setItem(
    `lostdc_server_scroll_${serverId}`,
    String(Math.round(window.scrollY))
  );
  navigate(-1);
}}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass text-[#FFBF00] hover:border-[#FFBF00]/40 transition"
        >
          <ArrowLeft size={14} />
          Back to Server
        </button>
      </div>
    );
  }

  const team = data.team;
  const owners = data.owners || [];
  const admins = data.admins || [];
  const members = data.members || [];
  const regularMembers = data.regular_members || [];

  const color = data.server?.theme_color || "#FFBF00";

  const displayName = team.display_name || team.name || "Unnamed Team";

  const viewer = data.viewer || {};
  const canManage = Boolean(
    viewer.is_global_admin ||
    viewer.is_owner ||
    viewer.is_admin
  );

  const reloadHub = async () => {
    const result = await fetchTeamHub(serverId, teamId);
    setData(result);
    setNameDraft(
      result?.team?.display_name ||
      result?.team?.name ||
      ""
    );

    setDescriptionDraft(
      result?.team?.custom_description ||
      result?.team?.description ||
      ""
    );

    setTagDraft(result?.team?.tag || "");
    return result;
  };

  const handleBrandingUpload = async (kind, file) => {
    if (!file) return;

    const maxBytes =
      kind === "logo"
        ? 5 * 1024 * 1024
        : 8 * 1024 * 1024;

    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
      setBrandingError(
        "Only PNG, JPEG, and WebP images are supported."
      );
      setBrandingMessage("");
      return;
    }

    if (file.size > maxBytes) {
      setBrandingError(
        `${kind === "logo" ? "Logo" : "Banner"} must be smaller than ${
          kind === "logo" ? "5 MB" : "8 MB"
        }.`
      );
      setBrandingMessage("");
      return;
    }

    setBrandingLoading((prev) => ({
      ...prev,
      [kind]: true,
    }));
    setBrandingError("");
    setBrandingMessage("");

    try {
      await uploadTeamBranding(
        serverId,
        teamId,
        kind,
        file
      );

      await reloadHub();

      setBrandingMessage(
        `${kind === "logo" ? "Team logo" : "Team banner"} updated successfully.`
      );
    } catch (err) {
      setBrandingError(
        err?.response?.data?.detail ||
        `Could not update the team ${
          kind === "logo" ? "logo" : "banner"
        }.`
      );
    } finally {
      setBrandingLoading((prev) => ({
        ...prev,
        [kind]: false,
      }));
    }
  };

  const handleBrandingRemove = async (kind) => {
    if (!window.confirm(
      `Remove the team ${kind}?`
    )) {
      return;
    }

    setBrandingLoading((prev) => ({
      ...prev,
      [kind]: true,
    }));
    setBrandingError("");
    setBrandingMessage("");

    try {
      await deleteTeamBranding(
        serverId,
        teamId,
        kind
      );

      await reloadHub();

      setBrandingMessage(
        `${kind === "logo" ? "Team logo" : "Team banner"} removed.`
      );
    } catch (err) {
      setBrandingError(
        err?.response?.data?.detail ||
        `Could not remove the team ${
          kind === "logo" ? "logo" : "banner"
        }.`
      );
    } finally {
      setBrandingLoading((prev) => ({
        ...prev,
        [kind]: false,
      }));
    }
  };

  const triggerBrandingUpload = (kind) => {
    const input = document.createElement("input");

    input.type = "file";
    input.accept = "image/png,image/jpeg,image/webp";

    input.onchange = (event) => {
      const file = event.target?.files?.[0];

      if (file) {
        handleBrandingUpload(kind, file);
      }
    };

    input.click();
  };

  const saveSettings = async () => {
    setSaving(true);
    setMessage("");
    setMessageError("");

    try {
      const saveResult = await updateTeamHubSettings(
        serverId,
        teamId,
        {
          name: nameDraft.trim(),
          description: descriptionDraft,
          tag: tagDraft,
        }
      );

      setEditMode(false);
      setMessage("Team settings saved.");

      // If the team name changed, redirect using the
      // canonical slug generated by the backend.
      if (saveResult?.ok && saveResult?.new_team_slug) {
        navigate(
          `/teams/${encodeURIComponent(serverId)}/${encodeURIComponent(saveResult.new_team_slug)}`,
          { replace: true }
        );
        return;
      }

      await reloadHub();
    } catch (err) {
      setMessageError(
        err?.response?.data?.detail ||
        "Could not save team settings."
      );
    } finally {
      setSaving(false);
    }
  };

  const handlePlayerSearch = async (value) => {
    setInviteQuery(value);
    setMessage("");
    setMessageError("");

    if (value.trim().length < 2) {
      setInvitePlayers([]);
      return;
    }

    setInviteLoading(true);

    try {
      const result = await searchTeamPlayers(
        serverId,
        teamId,
        value.trim()
      );

      setInvitePlayers(result?.players || []);
    } catch (err) {
      setInvitePlayers([]);
      setMessageError(
        err?.response?.data?.detail ||
        "Could not search players."
      );
    } finally {
      setInviteLoading(false);
    }
  };

  const handleInvite = async (username) => {
    if (!username) return;

    setActionLoading(true);
    setMessage("");
    setMessageError("");

    try {
      await inviteTeamPlayer(
        serverId,
        teamId,
        username
      );

      setInviteQuery("");
      setInvitePlayers([]);

      await reloadHub();

      setMessage(
        `Invitation sent to ${username}.`
      );
    } catch (err) {
      setMessageError(
        err?.response?.data?.detail ||
        "Could not invite player."
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleKick = async (username) => {
    if (!username) return;

    if (!window.confirm(`Kick ${username} from this team?`)) {
      return;
    }

    setActionLoading(true);
    setMessage("");
    setMessageError("");

    try {
      await kickTeamPlayer(
        serverId,
        teamId,
        username
      );

      await reloadHub();

      setMessage(`${username} was kicked from the team.`);
    } catch (err) {
      setMessageError(
        err?.response?.data?.detail ||
        "Could not kick player."
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleTransfer = async (username) => {
    if (!username) return;

    if (
      !window.confirm(
        `Transfer team ownership to ${username}? This cannot be undone from this screen.`
      )
    ) {
      return;
    }

    setActionLoading(true);
    setMessage("");
    setMessageError("");

    try {
      await transferTeamOwnership(
        serverId,
        teamId,
        username
      );

      await reloadHub();

      setMessage(
        `Ownership transferred to ${username}.`
      );
    } catch (err) {
      setMessageError(
        err?.response?.data?.detail ||
        "Could not transfer ownership."
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleLeave = async () => {
    if (
      !window.confirm(
        "Are you sure you want to leave this team?"
      )
    ) {
      return;
    }

    setActionLoading(true);
    setMessage("");
    setMessageError("");

    try {
      await leaveTeam(serverId, teamId);

      setMessage("You left the team.");

      await reloadHub();
      setEditMode(false);
    } catch (err) {
      setMessageError(
        err?.response?.data?.detail ||
        "Could not leave the team."
      );
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="pb-24">

      {/* Banner */}
      <div className="relative overflow-hidden">
        {team.banner_url ? (
          <div className="h-64 sm:h-80">
            <img
              src={team.banner_url}
              alt=""
              className="w-full h-full object-cover"
            />

            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
          </div>
        ) : (
          <div
            className="h-64 sm:h-80"
            style={{
              background: `linear-gradient(135deg, ${color}22, rgba(0,0,0,.9))`,
            }}
          />
        )}

        <div className="absolute inset-x-0 bottom-0">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-8">

            <button
              type="button"
              onClick={() => {
  sessionStorage.setItem(
    `lostdc_server_scroll_${serverId}`,
    String(Math.round(window.scrollY))
  );
  navigate(-1);
}}
              className="inline-flex items-center gap-2 text-xs text-white/50 hover:text-white mb-6 transition"
            >
              <ArrowLeft size={14} />
              Back to Server
            </button>

            <div className="flex flex-col sm:flex-row sm:items-end gap-5">

              {team.logo_url ? (
                <img
                  src={team.logo_url}
                  alt=""
                  className="w-24 h-24 sm:w-28 sm:h-28 rounded-3xl object-cover border border-white/15 shadow-2xl"
                />
              ) : (
                <div
                  className="w-24 h-24 sm:w-28 sm:h-28 rounded-3xl flex items-center justify-center text-4xl font-black border border-white/15"
                  style={{
                    background: `${color}20`,
                    color,
                  }}
                >
                  {displayName.charAt(0).toUpperCase()}
                </div>
              )}

              <div>
                <div className="flex items-center gap-3 mb-3">
                  <div
                    className="px-3 py-1.5 rounded-full border text-[10px] font-black uppercase tracking-[0.25em]"
                    style={{
                      color,
                      borderColor: `${color}55`,
                      background: `${color}12`,
                    }}
                  >
                    TEAMS
                  </div>

                  <div className="text-[10px] uppercase tracking-[0.25em] text-white/30">
                    CHAMPIONSHIP RANK
                  </div>
                </div>

                <div className="flex items-end gap-3 mb-2">
                  <span
                    className="text-5xl sm:text-7xl font-black leading-none"
                    style={{ color }}
                  >
                    #{team.rank || "—"}
                  </span>

                  <span className="pb-1 text-[10px] uppercase tracking-[0.25em] text-white/30">
                    RANK
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <h1 className="text-3xl sm:text-5xl font-black">
                    {displayName}
                  </h1>

                  {canManage && (
                    <button
                      type="button"
                      onClick={() => {
                        setEditMode((value) => !value);
                        setMessage("");
                        setMessageError("");
                      }}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl glass text-xs font-bold hover:bg-white/10 transition"
                    >
                      <Settings size={14} />
                      {editMode ? "Close Edit" : "Edit Team"}
                    </button>
                  )}
                </div>

                {team.tag && (
                  <div className="text-white/45 mt-2">
                    Team Tag: <span className="text-white/70">{team.tag}</span>
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 mt-8">

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">

          <Stat
            icon={<Trophy size={18} />}
            label="Rank"
            value={`#${team.rank || "—"}`}
            color={color}
          />

          <Stat
            icon={<Trophy size={18} />}
            label="Score"
            value={Number(team.score || 0).toLocaleString()}
            color={color}
          />

          <Stat
            icon={<Users size={18} />}
            label="Members"
            value={Number(team.member_count || members.length)}
            color={color}
          />

          <Stat
            icon={<Shield size={18} />}
            label="Level"
            value={Number(team.level || 1)}
            color={color}
          />

          <Stat
            icon={<Coins size={18} />}
            label="Money"
            value={Number(team.money || 0).toLocaleString()}
            color={color}
          />

        </div>

        {canManage && editMode && (
          <section className="mt-8">
            <SectionTitle title="Team Management" />

            <div className="rounded-2xl glass p-5 sm:p-6 space-y-6">

              {(message || messageError) && (
                <div
                  className={`rounded-xl px-4 py-3 text-sm ${
                    messageError
                      ? "bg-red-500/10 text-red-300 border border-red-500/20"
                      : "bg-emerald-500/10 text-emerald-300 border border-emerald-500/20"
                  }`}
                >
                  {messageError || message}
                </div>
              )}

              {/* Team Name + Description + Tag */}
              <div className="grid lg:grid-cols-2 gap-5">

                <div>
                  <label className="block text-xs uppercase tracking-wider text-white/40 mb-2">
                    Team Name
                  </label>

                  <input
                    type="text"
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value.slice(0, 50))}
                    maxLength={50}
                    className="w-full rounded-xl bg-black/20 border border-white/10 px-4 py-3 text-sm outline-none focus:border-white/25"
                    placeholder="Enter team name..."
                  />

                  <div className="text-[11px] text-white/25 mt-1 text-right">
                    {nameDraft.length}/50
                  </div>
                </div>

                <div>
                  <label className="block text-xs uppercase tracking-wider text-white/40 mb-2">
                    Team Tag
                  </label>

                  <input
                    value={tagDraft}
                    onChange={(e) =>
                      setTagDraft(e.target.value.slice(0, 16))
                    }
                    maxLength={16}
                    className="w-full rounded-xl bg-black/20 border border-white/10 px-4 py-3 text-sm outline-none focus:border-white/25"
                    placeholder="Example: LDC"
                  />

                  <div className="text-[11px] text-white/25 mt-1">
                    Letters, numbers, _ and - only
                  </div>
                </div>

                <div className="lg:col-span-2">
                  <label className="block text-xs uppercase tracking-wider text-white/40 mb-2">
                    Team Description
                  </label>

                  <textarea
                    value={descriptionDraft}
                    onChange={(e) =>
                      setDescriptionDraft(e.target.value)
                    }
                    maxLength={300}
                    rows={5}
                    className="w-full rounded-xl bg-black/20 border border-white/10 px-4 py-3 text-sm outline-none focus:border-white/25 resize-none"
                    placeholder="Tell players about your team..."
                  />

                  <div className="text-[11px] text-white/25 mt-1 text-right">
                    {descriptionDraft.length}/300
                  </div>
                </div>

                <div>
                  <label className="block text-xs uppercase tracking-wider text-white/40 mb-2">
                    Team Tag
                  </label>

                  <input
                    value={tagDraft}
                    onChange={(e) =>
                      setTagDraft(e.target.value.slice(0, 16))
                    }
                    maxLength={16}
                    className="w-full rounded-xl bg-black/20 border border-white/10 px-4 py-3 text-sm outline-none focus:border-white/25"
                    placeholder="Example: LDC"
                  />

                  <div className="text-[11px] text-white/25 mt-1">
                    Letters, numbers, _ and - only
                  </div>
                </div>

              </div>

              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  disabled={saving || actionLoading}
                  onClick={saveSettings}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm transition disabled:opacity-50"
                  style={{
                    background: `${color}20`,
                    color,
                    border: `1px solid ${color}45`,
                  }}
                >
                  <Save size={15} />
                  {saving ? "Saving..." : "Save Changes"}
                </button>

                <button
                  type="button"
                  disabled={saving || actionLoading}
                  onClick={() => {
                    setNameDraft(
                      team.display_name ||
                      team.name ||
                      ""
                    );

                    setDescriptionDraft(
                      team.custom_description ||
                      team.description ||
                      ""
                    );

                    setTagDraft(team.tag || "");

                    setEditMode(false);
                    setMessage("");
                    setMessageError("");
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl glass text-sm hover:bg-white/10 transition disabled:opacity-50"
                >
                  <X size={15} />
                  Cancel
                </button>
              </div>

              {/* Team Branding */}
              <div className="border-t border-white/5 pt-6">
                <div className="flex items-center gap-2 mb-2">
                  <Globe size={16} style={{ color }} />
                  <h3 className="font-semibold">
                    Team Branding
                  </h3>
                </div>

                <p className="text-xs text-white/35 mb-5">
                  Customize the logo and banner displayed on your Team Hub.
                  PNG, JPEG, and WebP are supported.
                </p>

                {(brandingMessage || brandingError) && (
                  <div
                    className={`mb-5 rounded-xl border px-4 py-3 text-sm ${
                      brandingError
                        ? "border-red-400/20 bg-red-400/5 text-red-300"
                        : "border-emerald-400/20 bg-emerald-400/5 text-emerald-300"
                    }`}
                  >
                    {brandingError || brandingMessage}
                  </div>
                )}

                <div className="grid lg:grid-cols-2 gap-5">

                  {/* Logo */}
                  <div className="rounded-2xl border border-white/10 bg-black/10 p-4">
                    <div className="flex items-center justify-between gap-3 mb-4">
                      <div>
                        <div className="text-sm font-bold">
                          Team Logo
                        </div>
                        <div className="text-[11px] text-white/30 mt-1">
                          Maximum 5 MB
                        </div>
                      </div>

                      <div
                        className="w-14 h-14 rounded-xl overflow-hidden border border-white/10 flex items-center justify-center"
                        style={{
                          background: `${color}12`,
                        }}
                      >
                        {team.logo_url ? (
                          <img
                            src={team.logo_url}
                            alt="Team logo"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span
                            className="text-xl font-black"
                            style={{ color }}
                          >
                            {displayName.charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        disabled={brandingLoading.logo}
                        onClick={() =>
                          triggerBrandingUpload("logo")
                        }
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-bold transition disabled:opacity-50"
                        style={{
                          background: `${color}20`,
                          color,
                          border: `1px solid ${color}45`,
                        }}
                      >
                        <Edit3 size={14} />
                        {brandingLoading.logo
                          ? "Uploading..."
                          : team.logo_url
                            ? "Replace Logo"
                            : "Upload Logo"}
                      </button>

                      {team.logo_url && (
                        <button
                          type="button"
                          disabled={brandingLoading.logo}
                          onClick={() =>
                            handleBrandingRemove("logo")
                          }
                          className="inline-flex items-center gap-2 px-3 py-2 rounded-xl glass text-xs font-semibold text-red-300 hover:bg-red-400/10 transition disabled:opacity-50"
                        >
                          <X size={14} />
                          Remove
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Banner */}
                  <div className="rounded-2xl border border-white/10 bg-black/10 p-4">
                    <div className="flex items-center justify-between gap-3 mb-4">
                      <div>
                        <div className="text-sm font-bold">
                          Team Banner
                        </div>
                        <div className="text-[11px] text-white/30 mt-1">
                          Maximum 8 MB
                        </div>
                      </div>

                      {team.banner_url ? (
                        <div className="w-32 h-14 rounded-xl overflow-hidden border border-white/10">
                          <img
                            src={team.banner_url}
                            alt="Team banner"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : (
                        <div
                          className="w-32 h-14 rounded-xl border border-white/10"
                          style={{
                            background: `linear-gradient(135deg, ${color}25, rgba(0,0,0,.7))`,
                          }}
                        />
                      )}
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        disabled={brandingLoading.banner}
                        onClick={() =>
                          triggerBrandingUpload("banner")
                        }
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-bold transition disabled:opacity-50"
                        style={{
                          background: `${color}20`,
                          color,
                          border: `1px solid ${color}45`,
                        }}
                      >
                        <Edit3 size={14} />
                        {brandingLoading.banner
                          ? "Uploading..."
                          : team.banner_url
                            ? "Replace Banner"
                            : "Upload Banner"}
                      </button>

                      {team.banner_url && (
                        <button
                          type="button"
                          disabled={brandingLoading.banner}
                          onClick={() =>
                            handleBrandingRemove("banner")
                          }
                          className="inline-flex items-center gap-2 px-3 py-2 rounded-xl glass text-xs font-semibold text-red-300 hover:bg-red-400/10 transition disabled:opacity-50"
                        >
                          <X size={14} />
                          Remove
                        </button>
                      )}
                    </div>
                  </div>

                </div>
              </div>

              {/* Invite */}
              <div className="border-t border-white/5 pt-6">
                <div className="flex items-center gap-2 mb-3">
                  <UserPlus size={16} style={{ color }} />
                  <h3 className="font-semibold">
                    Invite Player
                  </h3>
                </div>

                <div className="relative max-w-xl">
                  <Search
                    size={15}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30"
                  />

                  <input
                    value={inviteQuery}
                    onChange={(e) =>
                      handlePlayerSearch(e.target.value)
                    }
                    className="w-full rounded-xl bg-black/20 border border-white/10 pl-10 pr-4 py-3 text-sm outline-none focus:border-white/25"
                    placeholder="Search verified Minecraft username..."
                  />

                  {inviteLoading && (
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-white/30">
                      Searching...
                    </div>
                  )}

                  {invitePlayers.length > 0 && (
                    <div className="absolute z-30 left-0 right-0 mt-2 rounded-xl glass border border-white/10 overflow-hidden shadow-2xl">
                      {invitePlayers.map((player) => (
                        <button
                          key={player.user_id || player.minecraft_username}
                          type="button"
                          disabled={actionLoading}
                          onClick={() =>
                            handleInvite(
                              player.minecraft_username
                            )
                          }
                          className="w-full px-4 py-3 text-left hover:bg-white/10 transition flex items-center justify-between gap-4"
                        >
                          <span className="font-medium">
                            {player.minecraft_username}
                          </span>

                          <span className="text-[10px] uppercase tracking-wider text-emerald-400/70">
                            Verified
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <p className="text-xs text-white/30 mt-2">
                  Only verified LostDC Minecraft accounts can be invited.
                </p>
              </div>

              {/* Member management */}
              <div className="border-t border-white/5 pt-6">
                <div className="flex items-center gap-2 mb-4">
                  <Users size={16} style={{ color }} />
                  <h3 className="font-semibold">
                    Manage Members
                  </h3>
                </div>

                <div className="space-y-2">
                  {members.map((member) => {
                    const username =
                      member.username ||
                      member.display_name ||
                      "";

                    const isSelf =
                      username.toLowerCase() ===
                      String(viewer.minecraft_username || "").toLowerCase();

                    const canKick =
                      !member.is_owner &&
                      !isSelf &&
                      (viewer.is_global_admin ||
                        viewer.is_owner ||
                        (viewer.is_admin && !member.is_admin));

                    return (
                      <div
                        key={member.uuid}
                        className="rounded-xl border border-white/5 bg-black/10 px-4 py-3 flex flex-wrap items-center justify-between gap-3"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div
                            className="w-9 h-9 rounded-xl flex items-center justify-center font-bold"
                            style={{
                              background: `${color}15`,
                              color,
                            }}
                          >
                            {username.charAt(0).toUpperCase() || "?"}
                          </div>

                          <div className="min-w-0">
                            <div className="font-medium truncate">
                              {username || member.uuid}
                            </div>

                            <div className="text-[11px] text-white/35">
                              {member.rank_name || "Member"}
                            </div>
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2">
                          {viewer.is_owner &&
                            !member.is_owner &&
                            !isSelf && (
                              <button
                                type="button"
                                disabled={actionLoading}
                                onClick={() =>
                                  handleTransfer(username)
                                }
                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs glass hover:bg-white/10 transition disabled:opacity-50"
                              >
                                <ArrowRightLeft size={13} />
                                Transfer
                              </button>
                            )}

                          {canKick && (
                            <button
                              type="button"
                              disabled={actionLoading}
                              onClick={() =>
                                handleKick(username)
                              }
                              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs text-red-300 bg-red-500/10 border border-red-500/15 hover:bg-red-500/15 transition disabled:opacity-50"
                            >
                              <UserMinus size={13} />
                              Kick
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Leave */}
              {viewer.is_member && (
                <div className="border-t border-white/5 pt-6">
                  <button
                    type="button"
                    disabled={actionLoading}
                    onClick={handleLeave}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm text-red-300 bg-red-500/10 border border-red-500/15 hover:bg-red-500/15 transition disabled:opacity-50"
                  >
                    <LogOut size={15} />
                    Leave Team
                  </button>
                </div>
              )}

            </div>
          </section>
        )}

        {/* Description */}
        {(team.custom_description || team.description) && (
          <section className="mt-10">
            <SectionTitle title="About the Team" />

            <div className="rounded-2xl glass p-6 text-white/65 leading-7">
              {team.custom_description || team.description}
            </div>
          </section>
        )}

        {/* Owners */}
        {owners.length > 0 && (
          <section className="mt-10">
            <SectionTitle title="Owners" />

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {owners.map((member) => (
                <MemberCard
                  key={member.uuid}
                  member={member}
                  icon={<Crown size={16} />}
                  color={color}
                  role="Owner"
                />
              ))}
            </div>
          </section>
        )}

        {/* Admins */}
        {admins.length > 0 && (
          <section className="mt-10">
            <SectionTitle title="Admins" />

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {admins.map((member) => (
                <MemberCard
                  key={member.uuid}
                  member={member}
                  icon={<Shield size={16} />}
                  color={color}
                  role="Admin"
                />
              ))}
            </div>
          </section>
        )}

        {/* Members */}
        {regularMembers.length > 0 && (
          <section className="mt-10">
            <SectionTitle title="Members" />

            <div className="rounded-2xl glass overflow-hidden">
              <div className="divide-y divide-white/5">
                {regularMembers.map((member) => (
                  <div
                    key={member.uuid}
                    className="px-5 py-4 flex items-center justify-between gap-4 hover:bg-white/[0.03]"
                  >
                    <div className="flex items-center gap-3 min-w-0">

                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center font-bold"
                        style={{
                          background: `${color}15`,
                          color,
                        }}
                      >
                        {(member.username ||
                          member.display_name ||
                          "?")
                          .charAt(0)
                          .toUpperCase()}
                      </div>

                      <div className="min-w-0">
                        <div className="font-medium truncate">
                          {member.username ||
                            member.display_name ||
                            member.uuid}
                        </div>

                        {member.title && (
                          <div className="text-xs text-white/35 mt-1">
                            {member.title}
                          </div>
                        )}
                      </div>

                    </div>

                    <span className="text-xs text-white/30">
                      Member
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Website */}
        
        {(team.website_url || team.discord_url) && (
          <section className="mt-10">
            <SectionTitle title="Team Links" />

            <div className="flex flex-wrap gap-3">

              {team.website_url && (
                <a
                  href={team.website_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-3 rounded-xl glass hover:bg-white/10 transition"
                >
                  <Globe size={16} />
                  Website
                  <ExternalLink size={13} />
                </a>
              )}

              {team.discord_url && (
                <a
                  href={team.discord_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-3 rounded-xl glass hover:bg-white/10 transition"
                >
                  <MessageCircle size={16} />
                  Discord
                  <ExternalLink size={13} />
                </a>
              )}

            </div>
          </section>
        )}

        {/* Team Activity */}
        {(data.activity || []).length > 0 && (
          <section className="mt-10">
            <SectionTitle title="Team Activity" />

            <div className="rounded-2xl glass overflow-hidden border border-white/5">
              <div className="divide-y divide-white/5">
                {(data.activity || []).map((item, index) => {
                  const activity = formatTeamActivity(item);

                  const actor =
                    item?.actor?.minecraft_username ||
                    item?.actor?.username ||
                    item?.actor_username ||
                    "Unknown";

                  const target =
                    item?.target?.minecraft_username ||
                    item?.target?.username ||
                    item?.target_username ||
                    "";

                  const createdAt = item?.created_at
                    ? new Date(item.created_at)
                    : null;

                  const validDate =
                    createdAt &&
                    !Number.isNaN(createdAt.getTime());

                  return (
                    <div
                      key={item._id || item.id || index}
                      className="px-5 py-4 flex items-start gap-4 hover:bg-white/[0.025] transition"
                    >
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                        style={{
                          background: `${color}15`,
                          color,
                        }}
                      >
                        <TeamActivityIcon
                          type={activity.icon}
                          color={color}
                        />
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-white/90">
                          {activity.title}
                        </div>

                        <div className="text-sm text-white/60 mt-1">
                          {activity.description}
                        </div>

                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          {actor && actor !== "Unknown" && (
                            <span
                              className="text-xs font-medium"
                              style={{ color }}
                            >
                              {actor}
                            </span>
                          )}

                          {target && (
                            <>
                              <span className="text-xs text-white/25">
                                →
                              </span>

                              <span className="text-xs text-white/45">
                                {target}
                              </span>
                            </>
                          )}

                          {validDate && (
                            <>
                              <span className="text-xs text-white/15">
                                •
                              </span>

                              <span className="text-[10px] text-white/25">
                                {createdAt.toLocaleString()}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

      </div>
    </div>
  );
}

function formatTeamActivity(item) {
  const type = String(item?.type || "").toLowerCase();

  const actor =
    item?.actor?.minecraft_username ||
    item?.actor_username ||
    "Someone";

  const target =
    item?.target?.minecraft_username ||
    item?.target_username ||
    "";

  const metadata = item?.metadata || {};

  switch (type) {
    case "team_settings_updated": {
      const oldName = metadata.old_name || "";
      const newName = metadata.new_name || "";

      if (
        oldName &&
        newName &&
        oldName !== newName
      ) {
        return {
          title: "Team renamed",
          description: `${actor} renamed the team from "${oldName}" to "${newName}".`,
          icon: "rename",
        };
      }

      const fields = Array.isArray(metadata.fields)
        ? metadata.fields
        : [];

      if (fields.length) {
        return {
          title: "Team settings updated",
          description: `${actor} updated ${fields.join(", ")}.`,
          icon: "settings",
        };
      }

      return {
        title: "Team settings updated",
        description: `${actor} updated the team settings.`,
        icon: "settings",
      };
    }

    case "description_update":
      return {
        title: "Description updated",
        description: `${actor} updated the team description.`,
        icon: "edit",
      };

    case "invite":
      return {
        title: "Player invited",
        description: target
          ? `${actor} invited ${target} to join the team.`
          : `${actor} sent a team invitation.`,
        icon: "invite",
      };

    case "member_kick":
      return {
        title: "Member kicked",
        description: target
          ? `${actor} kicked ${target} from the team.`
          : `${actor} removed a member from the team.`,
        icon: "kick",
      };

    case "member_leave":
      return {
        title: "Member left",
        description: `${actor} left the team.`,
        icon: "leave",
      };

    case "ownership_transfer":
      return {
        title: "Ownership transferred",
        description: target
          ? `${actor} transferred team ownership to ${target}.`
          : `${actor} transferred team ownership.`,
        icon: "transfer",
      };

    case "invite_accept":
      return {
        title: "Invitation accepted",
        description: `${actor} accepted the team invitation.`,
        icon: "accept",
      };

    default:
      return {
        title: type
          ? type
              .replace(/_/g, " ")
              .replace(/\b\w/g, (c) => c.toUpperCase())
          : "Team activity",
        description: target
          ? `${actor} → ${target}`
          : `${actor} performed a team action.`,
        icon: "history",
      };
  }
}


function TeamActivityIcon({ type, color }) {
  const iconClass = "w-4 h-4";

  switch (type) {
    case "rename":
      return <Edit3 size={iconClass ? 15 : 15} />;

    case "settings":
      return <Settings size={15} />;

    case "edit":
      return <Edit3 size={15} />;

    case "invite":
      return <UserPlus size={15} />;

    case "kick":
      return <UserMinus size={15} />;

    case "leave":
      return <LogOut size={15} />;

    case "transfer":
      return <ArrowRightLeft size={15} />;

    case "accept":
      return <UserCheck size={15} />;

    default:
      return <History size={15} />;
  }
}


function Stat({ icon, label, value, color }) {
  return (
    <div className="rounded-2xl glass p-5">
      <div
        className="flex items-center gap-2 text-xs uppercase tracking-wider"
        style={{ color }}
      >
        {icon}
        {label}
      </div>

      <div className="text-2xl font-bold mt-3">
        {value}
      </div>
    </div>
  );
}

function SectionTitle({ title }) {
  return (
    <h2 className="text-xl sm:text-2xl font-bold mb-4">
      {title}
    </h2>
  );
}

function MemberCard({ member, icon, color, role }) {
  const name =
    member.username ||
    member.display_name ||
    member.uuid ||
    "Unknown";

  return (
    <div className="rounded-2xl glass p-5 flex items-center gap-4">

      <div
        className="w-12 h-12 rounded-2xl flex items-center justify-center font-bold text-lg"
        style={{
          background: `${color}18`,
          color,
        }}
      >
        {name.charAt(0).toUpperCase()}
      </div>

      <div className="min-w-0">
        <div className="font-semibold truncate">
          {name}
        </div>

        <div
          className="flex items-center gap-1.5 text-xs mt-1"
          style={{ color }}
        >
          {icon}
          {role}
        </div>

        {member.title && (
          <div className="text-xs text-white/35 mt-1 truncate">
            {member.title}
          </div>
        )}
      </div>

    </div>
  );
}
